<template>
  <div class="video-card">
    <img
      v-if="!shouldLoad && thumbnailUrl"
      :src="thumbnailUrl"
      class="video-el"
      :class="{ 'video-el--contain': isLandscape }"
      alt=""
    />
    <video
      ref="videoEl"
      class="video-el"
      :class="{
        'video-loading': videoLoading,
        'video-el--contain': isLandscape,
      }"
      :src="shouldLoad ? url : undefined"
      :preload="lazyLoad ? 'none' : 'metadata'"
      playsinline
      :controls="shouldLoad"
      controlsList="nofullscreen"
      @ended="playing = false"
      @pause="playing = false"
      @play="playing = true"
      @playing="videoLoading = false"
      @waiting="videoLoading = true"
    />
    <div v-if="videoLoading" class="loading-spinner">
      <i class="pi pi-spin pi-spinner" />
    </div>
    <div v-if="!playing" class="shade" />
    <div v-if="!playing" class="click-catcher" @click="togglePlay" />

    <div class="badges">
      <div v-if="duration" class="dur-badge">{{ formattedDuration }}</div>
      <div v-if="width && height" class="dur-badge">
        {{ width }}×{{ height }}
      </div>
      <div v-if="createdAt" class="dur-badge">{{ formattedDate }}</div>
    </div>

    <div class="actions">
      <button
        v-for="action in resolvedActions"
        :key="action.key"
        class="action-btn"
        :class="{ 'action-btn-danger': action.danger }"
        :title="action.title"
        :disabled="action.disabled || action.loading"
        @click="onActionClick(action)"
      >
        <i v-if="action.loading" class="pi pi-spin pi-spinner" />
        <i v-else-if="action.icon" :class="action.icon" />
        <template v-else>{{ action.label }}</template>
      </button>
    </div>

    <button
      v-if="!playing"
      class="center-play"
      title="Play"
      @click="togglePlay"
    >
      <i class="pi pi-play" />
    </button>

    <div v-if="!playing && metaLines.length" class="meta">
      <p
        v-for="(line, index) in metaLines"
        :key="index"
        class="meta-line"
        :class="{ 'meta-line--muted': line.muted }"
      >
        <span v-if="line.label" class="meta-label">{{ line.label }}</span>
        {{ line.text }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from "vue";

const SECONDS_PER_MINUTE = 60;

export default {
  name: "VideoCard",
  props: {
    video: { type: Object, required: true },
    id: { type: [String, Number], required: true },
    url: { type: String, required: true },
    thumbnailUrl: { type: String, default: null },
    duration: { type: Number, default: null },
    width: { type: Number, default: null },
    height: { type: Number, default: null },
    createdAt: { type: [String, Date], default: null },
    metaLines: { type: Array, default: () => [] },
    downloadFilename: { type: String, default: "video.mp4" },
    activePlayingId: { type: [String, Number], default: null },
    deletingId: { type: [String, Number], default: null },
    lazyLoad: { type: Boolean, default: true },
    extraActions: { type: Array, default: () => [] },
  },
  emits: ["play", "copy-link", "delete", "action"],
  setup(props, { emit }) {
    const videoEl = ref(null);
    const playing = ref(false);
    const videoLoading = ref(false);
    const shouldLoad = ref(!props.lazyLoad);
    const deleting = computed(() => props.deletingId === props.id);
    const isLandscape = computed(
      () => !!props.width && !!props.height && props.width > props.height
    );
    const formattedDuration = computed(() => {
      const minutes = Math.floor(props.duration / SECONDS_PER_MINUTE);
      const seconds = String(props.duration % SECONDS_PER_MINUTE).padStart(
        2,
        "0"
      );
      return `${minutes}:${seconds}`;
    });
    const formattedDate = computed(() =>
      new Date(props.createdAt).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    );

    const resolvedActions = computed(() => [
      { key: "download", icon: "pi pi-download", title: "Download" },
      { key: "copy-link", icon: "pi pi-link", title: "Copy link" },
      ...props.extraActions,
      {
        key: "delete",
        icon: "pi pi-trash",
        title: "Delete",
        loading: deleting.value,
        danger: true,
      },
    ]);

    const startPlay = async () => {
      videoLoading.value = true;
      if (!shouldLoad.value) {
        shouldLoad.value = true;
        await nextTick();
      }
      videoEl.value.play().catch(() => {
        videoLoading.value = false;
      });
    };

    const togglePlay = () => {
      if (playing.value) {
        videoEl.value.pause();
        videoLoading.value = false;
      } else {
        emit("play", props.id);
        startPlay();
      }
    };

    watch(
      () => props.activePlayingId,
      (activeId) => {
        if (activeId !== props.id && playing.value) {
          videoEl.value.pause();
          videoLoading.value = false;
        }
      }
    );

    const download = async () => {
      try {
        const response = await fetch(props.url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = props.downloadFilename;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Error downloading video:", error);
        window.open(props.url, "_blank");
      }
    };

    const onActionClick = (action) => {
      if (action.key === "download") return download();
      if (action.key === "copy-link") return emit("copy-link", props.url);
      if (action.key === "delete") return emit("delete", props.video);
      emit("action", action.key, props.video);
    };

    return {
      videoEl,
      playing,
      videoLoading,
      shouldLoad,
      isLandscape,
      formattedDuration,
      formattedDate,
      resolvedActions,
      togglePlay,
      onActionClick,
    };
  },
};
</script>

<style scoped>
.video-card {
  position: relative;
  aspect-ratio: 9 / 16;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e7e8ec;
  background: #2a2c31;
}

.video-el {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.video-el--contain {
  object-fit: contain;
}

.video-el.video-loading {
  filter: blur(6px);
}

.click-catcher {
  position: absolute;
  inset: 0;
  bottom: 40px;
  z-index: 1;
  cursor: pointer;
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: none;
}

.loading-spinner i {
  color: #fff;
  font-size: 26px;
}

.shade {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0.55) 72%,
    rgba(0, 0, 0, 0.88) 100%
  );
}

.badges {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 2;
  pointer-events: none;
}

.dur-badge {
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 5px;
}

.center-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.32);
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  backdrop-filter: blur(1px);
  cursor: pointer;
}

.center-play i {
  color: #fff;
  font-size: 18px;
  margin-left: 3px;
}

.actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  z-index: 2;
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.action-btn i {
  font-size: 13px;
}

.action-btn-danger:hover {
  background: #e24c4c;
  border-color: #e24c4c;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.meta {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  color: #fff;
  z-index: 2;
  pointer-events: none;
}

.meta-label {
  color: #ff5a57;
  font-weight: 800;
  margin-right: 4px;
}

.meta-line {
  font-size: 12.5px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 3px 0;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta-line:last-child {
  margin-bottom: 0;
}

.meta-line--muted {
  font-weight: 600;
  letter-spacing: 0.2px;
  color: rgba(255, 255, 255, 0.75);
  text-shadow: none;
}
</style>
